import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectWorkspacePlan, selectWorkspaceRestrictions } from '../redux/workspace/workspace.selectors';
import { getPlan } from '../redux/workspace/workspace.plans';
import { togglePricingPlanUpgrade, enablePricingPlanLimit } from '../redux/main/main.actions';
import { trackEvent } from '../lib/analytics';
import { enableCanvasPricingPlanLimit } from '../redux/diagram/diagram.actions';

/**
 * Central frontend plan-gating hook.
 *   can(feature)          -> boolean capability
 *   cap(type)             -> number (-1 = unlimited)
 *   isAtCap(type, count)  -> boolean (false when unlimited)
 *   requireUpgrade(reason)-> open upgrade modal + track
 *   notifyCap(type, info) -> open the limit modal for a reached cap
 */
export function usePlanGate() {
    const dispatch = useDispatch();
    const plan = useSelector(selectWorkspacePlan) || 'free';
    const restrictions = useSelector(selectWorkspaceRestrictions) || getPlan(plan);

    const caps = restrictions?.caps || getPlan(plan).caps;
    const cans = restrictions?.can || getPlan(plan).can;

    const can = useCallback((feature) => !!cans?.[feature], [cans]);

    const cap = useCallback(
        (type) => (caps?.[type] ?? getPlan('free').caps[type]),
        [caps]
    );

    const isAtCap = useCallback((type, count) => {
        const c = caps?.[type] ?? getPlan('free').caps[type];
        if (c === -1) return false;
        return count >= c;
    }, [caps]);

    const requireUpgrade = useCallback((reason) => {
        trackEvent('app_plan_gate_upgrade', { page: 'app', type: 'gate', subtype: reason || 'unknown' });
        dispatch(togglePricingPlanUpgrade('main'));
    }, [dispatch]);

    const notifyCap = useCallback((type, info = {}, page = '') => {
        if (page === 'canvas') {
            dispatch(enableCanvasPricingPlanLimit({ type, current: info.current, max: info.max ?? caps?.[type] }));
        } else {
            dispatch(enablePricingPlanLimit({ type, current: info.current, max: info.max ?? caps?.[type] }));
        }
    }, [dispatch, caps]);

    return { plan, isPro: plan !== 'free', caps, can, cap, isAtCap, requireUpgrade, notifyCap };
}

export default usePlanGate;
